import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { isEqual } from 'lodash';
import {
  DependenciesMap,
  RelationshipType,
  initDependenciesMap,
} from '../components/DependenciesSection';
import { useTaskBundleDraft } from '../providers/TaskBundleProvider/TaskBundleProvider';

interface UseDependencyManagementProps {
  bundleId?: string;
  templateId?: string;
  isModalOpen: boolean;
  predecessors?: string[];
  successors?: string[];
}

export const useDependencyManagement = ({
  bundleId,
  templateId = '',
  isModalOpen,
  predecessors,
  successors,
}: UseDependencyManagementProps) => {
  const { newTaskTypes = false, taskDependenciesInBundles = false } =
    useFlags();
  const { selectors } = useTaskBundleDraft();

  const showDependencies = newTaskTypes && taskDependenciesInBundles;

  const getTemplateDependencies = selectors.getTemplateDependencies;

  // Snapshot the dependencies at the moment the modal opens rather than
  // deriving them on every render. This prevents any provider state change
  // (e.g. an unrelated field edit while the modal is open) from recomputing
  // initialDeps to a new object reference, which would otherwise fire the
  // useEffect below and discard the user's in-progress dropdown picks.
  const snapshotRef = useRef<DependenciesMap | null>(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isModalOpen && !wasOpenRef.current) {
      // Modal just opened — take a snapshot of current dependencies
      const draftDeps = templateId
        ? getTemplateDependencies(templateId)
        : undefined;
      snapshotRef.current = initDependenciesMap(
        draftDeps?.predecessors,
        draftDeps?.successors
      );
    }
    if (!isModalOpen) {
      snapshotRef.current = null;
    }
    wasOpenRef.current = isModalOpen;
  }, [isModalOpen, templateId, getTemplateDependencies]);

  const initialDeps = useMemo<DependenciesMap>(() => {
    if (snapshotRef.current !== null) return snapshotRef.current;
    if (!templateId) return {};
    const draftDeps = getTemplateDependencies(templateId);
    return initDependenciesMap(draftDeps?.predecessors, draftDeps?.successors);
  }, [templateId, getTemplateDependencies]);

  const [dependenciesMap, setDependenciesMap] =
    useState<DependenciesMap>(initialDeps);

  useEffect(() => {
    if (isModalOpen && snapshotRef.current !== null) {
      setDependenciesMap(snapshotRef.current);
    }
  }, [isModalOpen]);

  const dependenciesChanged = useMemo(() => {
    return !(
      isEqual(dependenciesMap.depends_on, initialDeps.depends_on) &&
      isEqual(dependenciesMap.blocks, initialDeps.blocks)
    );
  }, [dependenciesMap, initialDeps]);

  const handleDependencyChange = useCallback(
    (type: RelationshipType, taskIds: string[]) => {
      setDependenciesMap((prev) => {
        if (taskIds.length === 0) {
          const next = { ...prev };
          delete next[type];
          return next;
        }
        return { ...prev, [type]: taskIds };
      });
    },
    []
  );

  const getMappedDependencies = useCallback((): {
    predecessors: string[];
    successors: string[];
  } => {
    if (!showDependencies) {
      return { predecessors: predecessors ?? [], successors: successors ?? [] };
    }
    return {
      predecessors: dependenciesMap.depends_on ?? [],
      successors: dependenciesMap.blocks ?? [],
    };
  }, [showDependencies, dependenciesMap, predecessors, successors]);

  const getAvailableTasks = useCallback(() => {
    if (!templateId) {
      return selectors.templates;
    }
    return selectors.templates.filter((t) => t.id !== templateId);
  }, [templateId, selectors.templates]);

  const resetDependencies = useCallback(() => {
    setDependenciesMap({});
  }, []);

  const dependenciesCount = useMemo(() => {
    return Object.values(dependenciesMap).reduce(
      (acc, curr) => acc + curr.length,
      0
    );
  }, [dependenciesMap]);

  return {
    showDependencies,
    dependenciesMap,
    dependenciesChanged,
    handleDependencyChange,
    getMappedDependencies,
    getAvailableTasks,
    resetDependencies,
    dependenciesCount,
  };
};
